import { FC, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox } from 'antd';
import { useParams } from 'react-router-dom';
import {
  useProcInstanceContext,
  useUserProcStepContext,
} from '@/entities/proc';
import { useProcComponentFill } from '@/shared/api';
import { UserInstanceParams } from '@/shared/types';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  id: string;
  label?: string;
  isChecked?: boolean;
};

export const CheckboxInputMode: FC<Props> = ({
  id,
  label,
  isChecked,
  ...rest
}) => {
  const queryClient = useQueryClient();
  const { code } = useParams<UserInstanceParams>();
  const { procInstance } = useProcInstanceContext();
  const { stepId } = useUserProcStepContext();

  const [value, setValue] = useState(isChecked);

  const { mutate: updateProcComponent } = useProcComponentFill();

  const queryKey = useMemo(
    () =>
      code
        ? ['instances', procInstance?.id, { code }]
        : ['instances', procInstance?.id],
    [code],
  );

  const handleSubmit = (checked: boolean) => {
    setValue(checked);
    try {
      updateProcComponent(
        {
          pathParams: { instanceId: procInstance?.id!, stepId: stepId! },
          queryParams: { code },
          body: {
            ...rest,
            id,
            label,
            isChecked: checked,
          },
        },
        {
          // refetch because of new logic
          onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'checkbox',
        action: 'submit',
      });
    }
  };

  return (
    <div data-testid="checkbox-component">
      <Checkbox
        className="ss-checkbox"
        checked={value}
        onChange={(e) => handleSubmit(e.target.checked)}
      >
        {label}
      </Checkbox>
    </div>
  );
};
